"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { Save, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { SmartUserDisplay } from "@/components/shared/smart-display/SmartUserDisplay";
import type { SimpleFieldConfig } from "../../../UniversalCRMTable/types";

interface FieldRowEditingProps {
  fieldId: string;
  fieldConfig: SimpleFieldConfig;
  value: unknown;
  isFullscreen: boolean;
  renderInput: (fieldId: string, fc: SimpleFieldConfig, v: unknown) => React.ReactNode;
  onSave: (fieldId: string) => void;
  onCancel: () => void;
}

export function FieldRowEditing({
  fieldId,
  fieldConfig,
  value,
  isFullscreen,
  renderInput,
  onSave,
  onCancel,
}: FieldRowEditingProps) {
  return (
    <div className="space-y-3">
      {fieldConfig.type === "multi-user" && value && Array.isArray(value) && value.length > 0 ? (
        <div className="p-3 bg-card rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Currently Selected:</div>
          <div className="flex gap-1 flex-wrap">
            {value.map((userItem: unknown, index: number) => {
              if (typeof userItem === "object" && userItem !== null && "id" in userItem) {
                const u = userItem as { id: string };
                return (
                  <SmartUserDisplay
                    key={index}
                    userId={u.id}
                    userData={u}
                    showIcon
                    className="inline-block"
                  />
                );
              }
              return (
                <SmartUserDisplay
                  key={index}
                  userId={String(userItem)}
                  showIcon
                  className="inline-block"
                />
              );
            })}
          </div>
        </div>
      ) : null}
      <div
        className={cn(
          "p-4 rounded-lg border",
          isFullscreen ? "bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700" : "bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800"
        )}
      >
        <div className="mb-3">{renderInput(fieldId, fieldConfig, value)}</div>
        {fieldConfig.type !== "tags" && (
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() => onSave(fieldId)}
              className={cn(
                "flex-1",
                isFullscreen ? "bg-gray-800 hover:bg-gray-900" : "bg-blue-600 hover:bg-blue-700"
              )}
            >
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onCancel}
              className="flex-1"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
